<template>
    <FutureDeliveryRequestDetail
        v-if="loaded"
        :show="show"
        :future-request="futureRequest"
        :show-delete-button="hasCompanyRole"
        :show-confirm-button="hasCompanyRole"
        :material-types="materialTypes"
        :material-size-types="materialSizeTypes"
        @update:show="$emit('update:show', $event)"
        @updateFutureDeliveryRequest="modifyFutureDeliveryRequest"
        @assign-to-receipt-logs="assignToReceiptLogs"
        @cancel="$emit('cancel')"
        @deleteRequest="deleteRequest"
        @confirmRequest="confirmRequest"
    />
</template>
<script>
import SmartyStreetsSDK from "smartystreets-javascript-sdk";
import { mapActions } from "vuex";
import swal from "sweetalert2";
import FutureDeliveryRequestDetail from "./_components/FutureDeliveryRequestDetail.vue";
import { getters } from "src/global-store.js";

const SmartyStreetsCore = SmartyStreetsSDK.core;
const { Lookup } = SmartyStreetsSDK.usStreet;

const credentials = new SmartyStreetsCore.SharedCredentials(
    process.env.VUE_APP_SMARTY_WEBSITE_KEY,
);
const usStreetClientBuilder = new SmartyStreetsCore.ClientBuilder(credentials);
const streetClient = usStreetClientBuilder.buildUsStreetApiClient();

export default {
    name: "NewDeliveryRequestModule",
    components: { FutureDeliveryRequestDetail },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        futureRequest: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            materialTypes: [],
            materialSizeTypes: ["Boxes", "Cubes", "Each", "Pallets"],
            loaded: false,
        };
    },
    computed: {
        hasCompanyRole() {
            return getters.hasCompanyRole();
        },
    },
    async created() {
        this.materialTypes = await this.fetchMaterialTypes();
        this.loaded = true;
    },
    methods: {
        ...mapActions("$_futureDeliveryRequest", [
            "updateFutureDeliveryRequest",
            "deleteFutureDeliveryRequest",
            "confirmFutureDeliveryRequest",
            "fetchMaterialTypes",
            "assignFutureDeliveryRequestToReceiptLogs",
        ]),
        async assignToReceiptLogs(receiptLogs) {
            await this.assignFutureDeliveryRequestToReceiptLogs({
                id: this.futureRequest.id,
                receiptLogIds: receiptLogs.map((log) => log.id),
            });
            this.$emit("update:show", false);
        },
        async modifyFutureDeliveryRequest(request) {
            let sendRequest = false;
            if (process.env.NODE_ENV !== "production") {
                // bypass address verification
                sendRequest = true;
            } else {
                const addressChanged =
                    this.futureRequest.deliveryAddress !==
                        request.deliveryAddress ||
                    this.futureRequest.deliveryCity !== request.deliveryCity ||
                    this.futureRequest.deliveryZip !== request.deliveryZip;

                if (addressChanged) {
                    const lookup = new Lookup();
                    let addressLookup;
                    lookup.street = request.deliveryAddress;
                    lookup.city = request.deliveryCity;
                    lookup.zipCode = request.deliveryZip;
                    lookup.state = "CO";
                    lookup.match = "strict";
                    const response = await streetClient.send(lookup);
                    addressLookup = response.lookups[0];
                    const { result } = addressLookup;

                    // there was a match
                    if (result.length) {
                        const candidate = result[0];
                        if (
                            addressLookup.street !== candidate.deliveryLine1 ||
                            addressLookup.city !==
                                candidate.components.cityName ||
                            addressLookup.zipCode !==
                                candidate.components.zipCode
                        ) {
                            // entered address is not the same as the candidate
                            const { value } = await swal({
                                title: "Delivery Address Verification",
                                allowEscapeKey: false,
                                allowOutsideClick: false,
                                showConfirmButton: true,
                                confirmButtonText: "Use verified address",
                                confirmButtonColor: "#3085d6",
                                showCancelButton: true,
                                cancelButtonText: "Use my original address",
                                cancelButtonColor: "#ff8d72",
                                type: "warning",
                                html: `
                <div class="row mb-3">
                  <div class="col-12"><h3 class="mb-0">Original Address:</h3></div>
                  <div class="col-12">${addressLookup.street}</div>
                  <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
                </div>
                <div class="row">
                  <div class="col-12"><h3 class="mb-0">Verified Address:</h3></div>
                  <div class="col-12">${candidate.deliveryLine1}</div>
                  <div class="col-12">${candidate.lastLine}</div>
                </div>
                `,
                            });
                            if (value) {
                                request.deliveryAddress =
                                    candidate.deliveryLine1;
                                request.deliveryCity =
                                    candidate.components.cityName;
                                request.deliveryZip =
                                    candidate.components.zipCode;
                                request.deliveryAddressVerified = true;
                                sendRequest = true;
                            } else {
                                request.deliveryAddressVerified = false;
                                sendRequest = true;
                            }
                        } else {
                            // perfect match
                            request.deliveryAddressVerified = true;
                            sendRequest = true;
                        }
                    } else {
                        const { value } = await swal({
                            title: "Delivery Address Not Verified",
                            allowEscapeKey: false,
                            allowOutsideClick: false,
                            showConfirmButton: true,
                            confirmButtonText: "Use this address anyway",
                            confirmButtonColor: "#ec250d",
                            showCancelButton: true,
                            cancelButtonText: "Let me change the address",
                            cancelButtonColor: "#3085d6",
                            type: "error",
                            html: `
              <div class="col-12">${addressLookup.street}</div>
              <div class="col-12">${addressLookup.city}, ${addressLookup.state} ${addressLookup.zipCode}</div>
              `,
                        });
                        if (value) {
                            request.deliveryAddressVerified = false;
                            sendRequest = true;
                        }
                    }
                } else {
                    sendRequest = true;
                }
            }
            if (sendRequest) {
                await this.updateFutureDeliveryRequest(request);
                this.$emit("update:show", false);
                swal({
                    title: "Success!",
                    text: "Your future delivery request has been updated.",
                    buttonsStyling: false,
                    confirmButtonClass: "btn btn-success btn-fill",
                    type: "success",
                });
            }
        },
        async deleteRequest() {
            await this.deleteFutureDeliveryRequest(this.futureRequest.id);
            this.$emit("update:show", false);
        },
        async confirmRequest() {
            await this.confirmFutureDeliveryRequest(this.futureRequest.id);
            this.$emit("update:show", false);
        },
    },
};
</script>
